import { Button, Heading, Icon, Link, ProgressSpinner } from '@dittolive/anvil'
import { DatabaseIcon, PlusIcon } from '@phosphor-icons/react'
import React from 'react'

import type { App, AppsTableConfig } from '../../types'
import AppRow from './AppRow'

type EmptyStateConfig = {
  title: string
  description: string
}

type Props<T extends App> = {
  apps: readonly T[]
  isLoading?: boolean
  config: AppsTableConfig
  getAppUrl: (app: T) => string
  viewerCanCreateApp?: boolean
  emptyState?: EmptyStateConfig
  renderRowExtra?: (app: T) => React.ReactNode
}

export default function AppsTable<T extends App>({
  apps,
  isLoading = false,
  config,
  getAppUrl,
  viewerCanCreateApp = false,
  emptyState,
  renderRowExtra,
}: Props<T>) {
  const { databaseText } = config.terminology

  const renderEmptyState = () => {
    const title = emptyState?.title ?? `No ${config.terminology.databasesText}`
    const description =
      emptyState?.description ?? `Create a ${databaseText} to get started`

    return (
      <div
        className="bg-background-surface border-border-normal h-87.5 flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border shadow-lg"
        data-testid="noAppsMessage"
      >
        <Icon
          svg={<DatabaseIcon />}
          className="text-foreground-subtle mb-1 size-10"
        />
        <Heading level={3} className="mb-0">
          {title}
        </Heading>
        <p className="text-foreground-subtle">{description}</p>
      </div>
    )
  }

  const renderApps = () => {
    if (!apps.length) {
      return renderEmptyState()
    }

    return (
      <div className="divide-border-normal bg-background-surface border-border-normal flex flex-col divide-y overflow-hidden rounded-xl border shadow-lg">
        {apps.map((app) => (
          <AppRow
            key={app.id}
            app={app}
            url={getAppUrl(app)}
            config={config}
            renderExtra={renderRowExtra?.(app)}
          />
        ))}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-87.5 flex items-center justify-center">
        <ProgressSpinner className="size-6" />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-y-2">
      {config.paths.createApp && (
        <div className="flex w-full justify-end">
          <Button
            asChild
            variant="primary"
            disabled={!viewerCanCreateApp}
            data-testid="createAppLink"
          >
            <Link to={config.paths.createApp()}>
              New {databaseText}
              <Icon svg={<PlusIcon weight="bold" />} className="size-4" />
            </Link>
          </Button>
        </div>
      )}

      {renderApps()}
    </div>
  )
}
